import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import type { AdminUser } from '../../types/admin';
import { usersApi } from '../../api/adminApi';
import { getApiErrorMessage } from '../../api/client';
import { notify } from '../../components/Notifier';

interface ResetPasswordDialogProps {
  open: boolean;
  user: AdminUser | null;
  onClose: () => void;
}

/**
 * Admin đặt lại mật khẩu cho một user khác — KHÔNG cần biết mật khẩu cũ.
 * Tái dùng PUT /api/users/:id (đã hỗ trợ sẵn trường `password`), chỉ gửi
 * đúng trường này để không vô tình đụng tên/email/role.
 */
export function ResetPasswordDialog({ open, user, onClose }: ResetPasswordDialogProps): JSX.Element {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setPassword('');
    setConfirmPassword('');
    setError(null);
  }, [open]);

  const handleSubmit = async () => {
    if (!user) return;
    if (password.length < 8) {
      setError('Mật khẩu phải có ít nhất 8 ký tự');
      return;
    }
    if (password !== confirmPassword) {
      setError('Mật khẩu xác nhận không khớp');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await usersApi.update(user.id, { password });
      notify(`Đã đặt lại mật khẩu cho "${user.name}"`, 'success');
      onClose();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Không thể đặt lại mật khẩu'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Đặt lại mật khẩu — {user?.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Mật khẩu mới"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            required
            autoFocus
            helperText="Tối thiểu 8 ký tự"
            disabled={saving}
          />
          <TextField
            label="Xác nhận mật khẩu mới"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
            required
            disabled={saving}
          />
          {error && <Box sx={{ color: 'error.main', fontSize: 14 }}>{error}</Box>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Hủy
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Đặt lại mật khẩu
        </Button>
      </DialogActions>
    </Dialog>
  );
}
